
import React from "react";
import { Slash } from "lucide-react";
import { 
  Settings, 
  Printer, 
  Wrench, 
  Zap, 
  ShoppingCart 
} from "lucide-react";
import { PartFilter } from "@/data/parts";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

interface RequirementIconProps {
  type: "root" | "tools" | "wiring" | "printing" | "purchase";
  isActive: boolean;
  isFilterActive: boolean;
  iconSize: number;
  getCursorClass: () => string;
  handleIconClick: (filter: PartFilter) => void;
}

const RequirementIcon: React.FC<RequirementIconProps> = ({
  type,
  isActive,
  isFilterActive,
  iconSize,
  getCursorClass,
  handleIconClick
}) => {
  // Icon selection based on type
  let Icon = Settings;
  let filter: PartFilter = "requires-root";
  let color = "text-destructive";
  let tooltip = "Requires Root";
  
  // Set up icon configuration
  switch (type) {
    case "root":
      Icon = Settings;
      filter = "requires-root";
      color = "text-destructive";
      tooltip = "Requires Root";
      break;
    case "tools":
      Icon = Wrench;
      filter = "requires-tools";
      color = "text-purple-600 dark:text-[#D946EF]";
      tooltip = "Requires Tools";
      break;
    case "wiring":
      Icon = Zap;
      filter = "wiring";
      color = "text-yellow-400";
      tooltip = "Requires Wiring";
      break;
    case "printing":
      Icon = Printer;
      filter = "requires-printing";
      color = "text-blue-500 dark:text-[#0EA5E9]";
      tooltip = "Requires 3D Printing";
      break;
    case "purchase":
      Icon = ShoppingCart;
      filter = "requires-purchase";
      color = "text-lime-600 dark:text-green-400";
      tooltip = "Requires Purchase";
      break;
  }
  
  // Define inactive color - use specific override class
  const inactiveColor = "text-muted-foreground requirement-inactive";
  
  // Calculate the style class for the icon
  const iconClass = isFilterActive
    ? "text-muted-foreground requirement-inactive"
    : isActive
      ? color
      : inactiveColor;
      
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`mx-0.5 relative ${getCursorClass()}`}
            onClick={() => handleIconClick(filter)}
            aria-label={tooltip}
          >
            <Icon 
              size={iconSize} 
              className={iconClass}
            />
            {isFilterActive && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground requirement-inactive rotate-90">
                <Slash size={iconSize} className="stroke-[2.5]" />
              </div>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {isFilterActive 
              ? `Hide ${tooltip.replace('Requires ', '')}` 
              : tooltip}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RequirementIcon;
